import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import { GALLERY, GALLERY_CATEGORIES } from '@/data/content';
import { PortfolioImage } from '@/components/site/SiteBits';
import { Seo } from '@/components/site/Seo';
import FinalCTA from '@/components/site/FinalCTA';

export default function Gallery() {
  const [active, setActive] = useState('All');
  const items = active === 'All' ? GALLERY : GALLERY.filter((g) => g.category === active);

  return (
    <div data-testid="page-gallery">
      <Seo
        title="Completed Projects — Real Work Photos | HearthOak Construction"
        description="Real project photos from HearthOak Construction — door installs, painting, drywall, and finish carpentry completed across Fort Worth, Burleson & DFW."
        path="/gallery"
      />
      <section className="border-b border-brand-border bg-brand-bone">
        <div className="container-x py-14 lg:py-20">
          <div className="max-w-3xl">
            <div className="eyebrow mb-3">Project Gallery</div>
            <h1 className="h1">Real projects. Real photos.</h1>
            <p className="body-lg mt-5">
              A working selection of doors, drywall, painting, and finish carpentry work completed for homeowners across the DFW area. This gallery grows as new projects wrap up.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-2" data-testid="gallery-filters">
            {GALLERY_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`px-4 py-2 font-sans text-xs uppercase tracking-[0.18em] border transition-colors ${
                  active === c
                    ? 'bg-brand-charcoal text-brand-bone border-brand-charcoal'
                    : 'bg-brand-bone text-brand-charcoal border-brand-border hover:border-brand-charcoal'
                }`}
                data-testid={`gallery-filter-${c.toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-paper">
        <div className="container-x py-16 lg:py-24">
          {items.length === 0 ? (
            <div className="text-center py-16 border border-brand-border bg-brand-bone">
              <div className="font-serif text-2xl text-brand-charcoal">Real project photo goes here</div>
              <p className="mt-2 font-sans text-sm text-brand-muted">More projects in this category coming soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
              {items.map((g) => {
                const media = g.media || [{ type: 'image', src: g.src }];
                const photos = media.filter((m) => m.type !== 'video').length;
                const videos = media.length - photos;
                const badge = [
                  photos ? `${photos} photo${photos > 1 ? 's' : ''}` : '',
                  videos ? `${videos} video${videos > 1 ? 's' : ''}` : '',
                ].filter(Boolean).join(' · ');
                return (
                  <Link
                    key={g.id}
                    to={`/gallery/${g.id}`}
                    className="group block border border-brand-border bg-brand-bone hover:border-brand-charcoal transition-colors"
                    data-testid={`gallery-item-${g.id}`}
                  >
                    <div className="relative">
                      <PortfolioImage src={g.cover || g.src} className="aspect-[4/3]" alt={g.title} />
                      {videos > 0 && (
                        <span className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none" data-testid={`gallery-play-${g.id}`}>
                          <span className="w-14 h-14 rounded-full bg-brand-charcoal/70 flex items-center justify-center transition-colors group-hover:bg-brand-maroon">
                            <Play size={22} className="text-brand-bone ml-0.5" fill="currentColor" />
                          </span>
                        </span>
                      )}
                      {media.length > 1 && (
                        <span className="absolute bottom-3 right-3 z-10 bg-brand-charcoal/85 text-brand-bone font-sans text-[10px] uppercase tracking-[0.18em] px-2 py-1">
                          {badge}
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="text-[10px] tracking-[0.22em] uppercase text-brand-maroon">{g.category}</div>
                      <div className="mt-1 font-serif text-lg text-brand-charcoal group-hover:text-brand-maroon transition-colors">{g.title}</div>
                      <p className="mt-1 font-sans text-sm text-brand-muted leading-relaxed">{g.caption}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
